import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Check, Clock, Mic, Link2, Sparkles, Gift, Trash2, Users, ChevronRight, X, MessageCircle, Pencil } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { PlanPanel } from "@/components/PlanPanel";
import { Waveform } from "@/components/Waveform";
import { RelationshipPicker, OTHER, ROLES, RELATIONSHIPS, cleanLabel, labelError } from "@/components/RelationshipPicker";
import { LabelEditModal } from "@/components/LabelEditModal";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, saveRelationship } from "@/lib/api";
import { SignedImg } from "@/lib/media";
import { toast } from "sonner";


export default function Dashboard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", relationship: "", birth_year: "" });
  const [saving, setSaving] = useState(false);
  const [demoing, setDemoing] = useState(false);
  const [editing, setEditing] = useState(null);
  const { refresh } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    try { const r = await api.get("/family"); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const addMember = async () => {
    if (!form.name.trim() || !form.relationship) return toast.error("Please add a name and relationship.");
    const relErr = labelError(form.relationship, "rel");
    if (relErr) return toast.error(relErr);
    setSaving(true);
    try {
      const { data: m } = await api.post("/members", { ...form, relationship: cleanLabel(form.relationship) });
      setOpen(false); setForm({ name: "", relationship: "", birth_year: "" });
      navigate(`/member/${m.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const loadDemo = async () => {
    setDemoing(true);
    toast.info("Preparing the Sterling family archive — this takes a moment…");
    try { await api.post("/demo/seed"); await load(); toast.success("Demonstration family loaded."); }
    catch (e) { toast.error(apiError(e)); } finally { setDemoing(false); }
  };
  const clearAll = async () => {
    if (!window.confirm("Remove all family members and recordings? This cannot be undone.")) return;
    await api.delete("/demo/clear"); load();
  };
  const copyLink = (token, name) => {
    navigator.clipboard.writeText(`${window.location.origin}/reply/${token}`);
    toast.success(`Private link for ${name} copied.`);
  };

  if (!data) return (
    <div className="min-h-screen legacy-bg"><Navbar />
      <p role="status" className="text-center text-lg text-[#B9B3C8] pt-24" data-testid="dashboard-loading">Opening your family's memories…</p>
    </div>
  );
  const { family, creator, members, stats, plan } = data;
  const full = plan.spaces_left === 0;
  const total = Math.max(members.length * 2, 1);
  const pct = Math.round(((stats.memories + stats.replies) / total) * 100);
  const anyMemory = stats.memories > 0;

  const field = "w-full h-14 px-4 rounded-xl bg-[#1A1222] border border-[#33264A] text-[#EDE6F7] text-lg placeholder:text-[#8C84A0] focus:outline-none focus:border-[#8B2CFF]";
  const pill = (active) => `h-11 px-4 rounded-full border text-sm transition-colors ${active ? "gold-btn" : "ghost-gold"}`;

  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div>
            <p className="eyebrow mb-1">Memories Forever</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#EDE6F7]">{family.family_name}</h1>
            <p className="text-[#8C84A0] mt-1 flex flex-wrap items-center gap-x-3">
              <span data-testid="creator-label">Kept by {creator.name}{creator?.family_label ? ` · ${creator.family_label}` : ""}</span>
              <button data-testid="edit-creator-label-btn" onClick={() => setEditing({ self: true })}
                className="inline-flex items-center gap-1 text-sm text-[#B57BFF] hover:text-[#EDE6F7] transition-colors">
                <Pencil className="h-3.5 w-3.5" /> {creator?.family_label ? "Edit" : "Add what your family calls you"}
              </button>
            </p>
          </div>
        </div>

        {/* Warm stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat n={stats.members} label="Family voices added" testId="stat-members" />
          <Stat n={stats.replies} label="Memories returned" testId="stat-replies" />
          <Stat n={stats.awaiting} label="Still to come" testId="stat-awaiting" />
          <div className="glass-card p-6" data-testid="stat-complete">
            <p className="eyebrow mb-2">Our story</p>
            <div className="font-serif text-3xl text-[#EDE6F7] mb-2">{pct}% complete</div>
            <div className="h-1.5 rounded-full bg-[#2E2140] overflow-hidden">
              <div className="h-full bg-[#8B2CFF]" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>

        <PlanPanel plan={plan} />

        {/* Keepsake banner */}
        {anyMemory && (
          <div className="glass-card p-6 mb-10 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="assemble-banner">
            <div className="flex items-center gap-4">
              <div className="reel-spin"><Waveform bars={10} height={34} /></div>
              <div>
                <p className="font-serif text-2xl text-[#EDE6F7]">Bring your voices together</p>
                <p className="text-[#B9B3C8]">Choose the playback order, preview it and create your audio keepsake.</p>
              </div>
            </div>
            <button data-testid="keepsake-builder-btn" onClick={() => navigate("/keepsake-builder")}
              className="gold-btn h-14 px-7 rounded-full text-lg flex items-center">
              <Sparkles className="h-5 w-5 mr-2" /> Create your keepsake
            </button>
          </div>
        )}

        <h2 className="font-serif text-3xl text-[#EDE6F7] mb-6">Your family</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {members.map((m, i) => (
            <div key={m.id} className="glass-card p-7 flex flex-col" data-testid={`member-card-${i}`}>
              <div className="flex items-center gap-4 mb-5">
                <div className="h-16 w-16 rounded-full bg-[#1A1222] border border-[#33264A] overflow-hidden flex items-center justify-center">
                  {m.photo ? <SignedImg path={m.photo} alt="" className="h-full w-full object-cover" />
                           : <span className="font-serif text-2xl gold-text">{m.name[0]}</span>}
                </div>
                <div>
                  <p className="font-serif text-2xl text-[#EDE6F7]">{m.name}</p>
                  <p className="gold-text" data-testid={`member-relationship-${i}`}>{m.relationship}</p>
                  <button data-testid={`edit-relationship-btn-${i}`} onClick={() => setEditing({ member: m })}
                    className="mt-1 inline-flex items-center gap-1 text-sm text-[#8C84A0] hover:text-[#B57BFF] transition-colors">
                    <Pencil className="h-3.5 w-3.5" /> Edit relationship
                  </button>
                </div>
              </div>
              <div className="space-y-2 mb-6 flex-1">
                <Row ok={!!m.memory} okText={`Your message to ${m.name} recorded`} waitText={`Your message to ${m.name} not recorded yet`} />
                {m.memory && <Row ok={!!m.reply} okText={`${m.name}'s reply received`} waitText={m.invited_at ? `Invitation sent · waiting for ${m.name}'s reply` : `Waiting for ${m.name}'s reply`} />}
                {m.is_demo && <span className="inline-block text-xs px-2 py-1 rounded-full bg-[#1A1222] text-[#B57BFF] border border-[rgba(139,44,255,0.3)]">Demonstration</span>}
              </div>
              <div className="flex flex-col gap-2">
                {!m.memory ? (
                  <button data-testid={`record-memory-btn-${i}`} onClick={() => navigate(`/member/${m.id}`)}
                    className="gold-btn h-13 py-3 rounded-full text-lg flex items-center justify-center">
                    <Mic className="h-5 w-5 mr-2" /> Record Memory
                  </button>
                ) : (
                  <button data-testid={`open-chapter-btn-${i}`} onClick={() => navigate(`/member/${m.id}`)}
                    className="ghost-gold h-13 py-3 rounded-full text-lg">Open Chapter</button>
                )}
                {m.memory && !m.reply && (
                  <div className="flex gap-2">
                    <a data-testid={`whatsapp-btn-${i}`}
                      href={`https://wa.me/?text=${encodeURIComponent(`I've left a family memory for you on Memories Forever. Please reply with your first memory of me: ${window.location.origin}/reply/${m.invite_token}`)}`}
                      target="_blank" rel="noreferrer"
                      className="flex-1 h-11 rounded-full text-[#8C84A0] hover:text-[#B57BFF] flex items-center justify-center transition-colors">
                      <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
                    </a>
                    <button data-testid={`copy-link-btn-${i}`} onClick={() => copyLink(m.invite_token, m.name)}
                      className="flex-1 h-11 rounded-full text-[#8C84A0] hover:text-[#B57BFF] flex items-center justify-center transition-colors">
                      <Link2 className="h-4 w-4 mr-2" /> Copy link
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          <button data-testid="add-member-btn" onClick={() => setOpen(true)} disabled={full}
            className="glass-card p-7 flex flex-col items-center justify-center gap-4 min-h-[16rem] border-dashed disabled:opacity-60 disabled:cursor-not-allowed">
            <div className="h-16 w-16 rounded-full gold-btn flex items-center justify-center"><Plus className="h-8 w-8" /></div>
            <span className="font-serif text-2xl text-[#EDE6F7]">{full ? "All spaces used" : "Add someone to receive a message"}</span>
            <span className="text-sm text-[#A49CB8]" data-testid="add-member-spaces">
              {full ? (plan.extension ? `You've used all ${plan.recipient_limit} spaces.` : "Add six more people for £4.99 above.") : `${plan.spaces_left} of ${plan.recipient_limit} spaces left`}
            </span>
          </button>
        </div>

        <div className="mt-14 flex flex-wrap gap-4 items-center justify-center">
          {members.length === 0 ? (
            <button data-testid="load-demo-btn" onClick={loadDemo} disabled={demoing}
              className="ghost-gold h-14 px-7 rounded-full text-lg flex items-center">
              <Users className="h-5 w-5 mr-2" /> {demoing ? "Loading demo…" : "See the Sterling demonstration family"}
            </button>
          ) : (
            <button data-testid="clear-all-btn" onClick={clearAll} className="text-[#7A2E4F] hover:text-[#EF4444] flex items-center">
              <Trash2 className="h-4 w-4 mr-2" /> Remove all &amp; start fresh
            </button>
          )}
        </div>
      </main>

      {editing?.member && (
        <LabelEditModal testid="edit-relationship-modal" kind="rel" options={RELATIONSHIPS} initial={editing.member.relationship}
          title="Edit relationship" intro={`How is ${editing.member.name} related to you? Their recordings stay exactly as they are.`}
          inputLabel="What is their relationship to you?" placeholder="e.g. Niece, Godson, Stepdaughter"
          onSave={async (v) => { await saveRelationship(editing.member.id, v); await load(); }} onClose={() => setEditing(null)} />
      )}
      {editing?.self && (
        <LabelEditModal testid="edit-creator-label-modal" kind="role" options={ROLES} initial={creator?.family_label}
          title="What does your family call you?" intro="This is shown to your family on their invitations and in your keepsake."
          inputLabel="What does your family call you?" placeholder="e.g. Auntie Jean, Papa John"
          onSave={async (v) => { await api.put("/profile", { family_label: v }); toast.success("Saved."); await Promise.all([load(), refresh()]); }}
          onClose={() => setEditing(null)} />
      )}

      {/* Add member modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="glass-card p-6 sm:p-8 max-w-md w-full max-h-[calc(100dvh-2rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-member-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-serif text-3xl text-[#EDE6F7]">Add a family member</h3>
              <button onClick={() => setOpen(false)} className="text-[#8C84A0] hover:text-[#EDE6F7]"><X className="h-6 w-6" /></button>
            </div>
            <p className="text-[#B9B3C8] mb-6">Just a name and how they're related to you.</p>
            <div className="space-y-5">
              <div>
                <label className="eyebrow block mb-2">Name</label>
                <input data-testid="member-name-input" className={field} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Freddie" />
              </div>
              <div>
                <label className="eyebrow block mb-2">Relationship</label>
                <RelationshipPicker options={RELATIONSHIPS} value={form.relationship} testid="member-rel" pill={pill}
                  onChange={(v) => setForm((f) => ({ ...f, relationship: v }))}
                  inputLabel="What is their relationship to you?" errorKind="rel" placeholder="e.g. Niece, Godson, Stepdaughter" />
              </div>
              <div>
                <label className="eyebrow block mb-2">Approx. birth year (optional)</label>
                <input data-testid="member-year-input" className={field} value={form.birth_year}
                  onChange={(e) => setForm({ ...form, birth_year: e.target.value })} placeholder="1998" />
              </div>
              <button data-testid="save-member-btn" onClick={addMember} disabled={saving}
                className="gold-btn w-full h-14 rounded-full text-lg flex items-center justify-center disabled:opacity-60">
                {saving ? "Saving…" : <>Save &amp; Record Memory <ChevronRight className="h-5 w-5 ml-1" /></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ n, label, testId }) {
  return (
    <div className="glass-card p-6" data-testid={testId}>
      <div className="font-serif text-4xl text-[#B57BFF]">{n}</div>
      <p className="text-[#B9B3C8] mt-1 uppercase text-xs tracking-widest">{label}</p>
    </div>
  );
}
function Row({ ok, okText, waitText }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? <Check className="h-5 w-5 text-[#B57BFF]" /> : <Clock className="h-5 w-5 text-[#5E5670]" />}
      <span className={ok ? "text-[#EDE6F7]" : "text-[#8C84A0]"}>{ok ? okText : waitText}</span>
    </div>
  );
}
